import { __decorate } from "tslib";
import { isIOS } from '../../utils/device-detection';
import playerAPI from '../../core/player-api-decorator';
import DesktopFullScreen from './desktop';
import IOSFullScreen from './ios';
import { VideoEvent, UIEvent, EngineState } from '../../constants';
const DEFAULT_CONFIG = {
    exitFullScreenOnEnd: true,
    enterFullScreenOnPlay: false,
    exitFullScreenOnPause: false,
    pauseVideoOnFullScreenExit: false,
};
class FullScreenManager {
    constructor({ eventEmitter, engine, rootContainer, config, }) {
        this._exitFullScreenOnEnd = false;
        this._enterFullScreenOnPlay = false;
        this._exitFullScreenOnPause = false;
        this._pauseVideoOnFullScreenExit = false;
        this._eventEmitter = eventEmitter;
        this._engine = engine;
        if (config.disableFullScreen) {
            this._isEnabled = false;
        }
        else {
            this._isEnabled = true;
            const _config = {
                ...DEFAULT_CONFIG,
            };
            this._exitFullScreenOnEnd = _config.exitFullScreenOnEnd;
            this._enterFullScreenOnPlay = _config.enterFullScreenOnPlay;
            this._exitFullScreenOnPause = _config.exitFullScreenOnPause;
            this._pauseVideoOnFullScreenExit = _config.pauseVideoOnFullScreenExit;
        }
        this._onChange = this._onChange.bind(this);
        if (isIOS()) {
            this._element = this._engine.getElement();
            this._helper = new IOSFullScreen(this._engine.getElement(), this._onChange);
        }
        else {
            this._element = rootContainer.getElement();
            this._helper = new DesktopFullScreen(rootContainer.getElement(), this._onChange);
        }
        this._bindEvents();
    }
    _onChange(event) {
        if (event.target !== this._element) {
            return;
        }
        if (!this._helper.isInFullScreen && this._pauseVideoOnFullScreenExit) {
            this._engine.pause();
        }
        this._eventEmitter.emitAsync(UIEvent.FULL_SCREEN_STATE_CHANGED, this._helper.isInFullScreen);
    }
    _bindEvents() {
        this._unbindEvents = this._eventEmitter.bindEvents([
            [VideoEvent.STATE_CHANGED, this._processNextStateFromEngine],
            [VideoEvent.PLAY_REQUEST, this._enterOnPlayRequested],
        ], this);
    }
    _exitOnEnd() {
        if (this._exitFullScreenOnEnd && this.isInFullScreen) {
            this.exitFullScreen();
        }
    }
    _enterOnPlayRequested() {
        if (this._enterFullScreenOnPlay && !this.isInFullScreen) {
            this.enterFullScreen();
        }
    }
    _exitOnPauseRequested() {
        if (this._exitFullScreenOnPause && this.isInFullScreen) {
            this.exitFullScreen();
        }
    }
    _processNextStateFromEngine({ nextState, }) {
        switch (nextState) {
            case EngineState.ENDED: {
                this._exitOnEnd();
                break;
            }
            case EngineState.PAUSED: {
                this._exitOnPauseRequested();
                break;
            }
            /* ignore coverage */
            default:
                break;
        }
    }
    /**
     * Allow player try to exit full screen on pause
     * @example
     * player.play();
     * player.enableExitFullScreenOnPause();
     * player.enterFullScreen();
     * console.log(player.isInFullScreen) // true
     * player.pause();
     * console.log(player.isInFullScreen) // false
     */
    enableExitFullScreenOnPause() {
        this._exitFullScreenOnPause = true;
    }
    /**
     * Disallow player to exit full screen on pause
     * @example
     * player.play();
     * player.disableExitFullScreenOnPause();
     * player.enterFullScreen();
     * console.log(player.isInFullScreen) // true
     * player.pause();
     * console.log(player.isInFullScreen) // true
     */
    disableExitFullScreenOnPause() {
        this._exitFullScreenOnPause = false;
    }
    /**
     * Allow player try to exit full screen on end
     * @example
     * player.play();
     * player.enableExitFullScreenOnEnd();
     * player.enterFullScreen();
     * console.log(player.isInFullScreen) // true
     * console.log(player.isEnded); // true
     * console.log(player.isInFullScreen) // false
     */
    enableExitFullScreenOnEnd() {
        this._exitFullScreenOnEnd = true;
    }
    /**
     * Disallow player try to exit full screen on end
     * @example
     * player.play();
     * player.disableExitFullScreenOnEnd();
     * player.enterFullScreen();
     * console.log(player.isInFullScreen) // true
     * console.log(player.isEnded); // true
     * console.log(player.isInFullScreen) // true
     */
    disableExitFullScreenOnEnd() {
        this._exitFullScreenOnPause = false;
    }
    /**
     * Allow player try to exit full screen on end
     * @example
     * player.enableEnterFullScreenOnPlay();
     * console.log(player.isInFullScreen) // false
     * player.play();
     * console.log(player.isInFullScreen) // true
     */
    enableEnterFullScreenOnPlay() {
        this._enterFullScreenOnPlay = true;
    }
    /**
     * Disallow player try to exit full screen on end
     * @example
     * player.disableEnterFullScreenOnPlay();
     * console.log(player.isInFullScreen) // false
     * player.play();
     * console.log(player.isInFullScreen) // false
     */
    disableEnterFullScreenOnPlay() {
        this._enterFullScreenOnPlay = false;
    }
    /**
     * Allow player try to exit full screen on end
     * @example
     * player.play();
     * player.enablePauseVideoOnFullScreenExit();
     * player.enterFullScreen();
     * console.log(player.isInFullScreen) // true
     * player.pause();
     * console.log(player.isInFullScreen) // false
     */
    enablePauseVideoOnFullScreenExit() {
        this._pauseVideoOnFullScreenExit = true;
    }
    /**
     * Disallow player try to exit full screen on end
     * @example
     * player.play();
     * player.enablePauseVideoOnFullScreenExit();
     * player.enterFullScreen();
     * console.log(player.isInFullScreen) // true
     * player.pause();
     * console.log(player.isInFullScreen) // true
     */
    disablePauseVideoOnFullScreenExit() {
        this._pauseVideoOnFullScreenExit = false;
    }
    /**
     * Player would try to enter fullscreen mode.
     * Behavior of fullscreen mode on different platforms may differ.
     * @example
     * player.enterFullScreen();
     */
    enterFullScreen() {
        if (!this.isEnabled) {
            return;
        }
        this._helper.request();
    }
    /**
     * Player would try to exit fullscreen mode.
     * @example
     * player.exitFullScreen();
     */
    exitFullScreen() {
        if (!this.isEnabled) {
            return;
        }
        this._helper.exit();
    }
    /**
     * Return true if player is in full screen
     * @example
     * console.log(player.isInFullScreen); // false
     */
    get isInFullScreen() {
        if (!this.isEnabled) {
            return false;
        }
        return this._helper.isInFullScreen;
    }
    get isEnabled() {
        return this._helper.isEnabled && this._isEnabled;
    }
    destroy() {
        this._unbindEvents();
        this._helper.destroy();
    }
}
FullScreenManager.moduleName = 'fullScreenManager';
FullScreenManager.dependencies = ['eventEmitter', 'engine', 'rootContainer', 'config'];
__decorate([
    playerAPI()
], FullScreenManager.prototype, "enableExitFullScreenOnPause", null);
__decorate([
    playerAPI()
], FullScreenManager.prototype, "disableExitFullScreenOnPause", null);
__decorate([
    playerAPI()
], FullScreenManager.prototype, "enableExitFullScreenOnEnd", null);
__decorate([
    playerAPI()
], FullScreenManager.prototype, "disableExitFullScreenOnEnd", null);
__decorate([
    playerAPI()
], FullScreenManager.prototype, "enableEnterFullScreenOnPlay", null);
__decorate([
    playerAPI()
], FullScreenManager.prototype, "disableEnterFullScreenOnPlay", null);
__decorate([
    playerAPI()
], FullScreenManager.prototype, "enablePauseVideoOnFullScreenExit", null);
__decorate([
    playerAPI()
], FullScreenManager.prototype, "disablePauseVideoOnFullScreenExit", null);
__decorate([
    playerAPI()
], FullScreenManager.prototype, "enterFullScreen", null);
__decorate([
    playerAPI()
], FullScreenManager.prototype, "exitFullScreen", null);
__decorate([
    playerAPI()
], FullScreenManager.prototype, "isInFullScreen", null);
export default FullScreenManager;
//# sourceMappingURL=full-screen-manager.js.map